import React, { useMemo } from "react";
import { Edit3 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import TableTemplate from "../common/TableTemplate";
import { generateDynamicColumns } from "../../utils/generateDynamicColumns";
import { columnHelper } from "../../utils/utils";
import MarketLabChartTemplate from "../charts/MarketLabChartTemplate";
import { transformLocalChartData } from "../../utils/marketLabChartUtils";
import toast from "react-hot-toast";

const MarketProductAnalytics = ({
  allAdsData,
  pagination,
  isLoading,
  chartData,
  onServerSearch,
  serverSearchValue,
  onServerFilter,
  serverFilters,
  onServerSort,
  serverSortBy,
  isPaginated,
}) => {
  // Generate columns from API column headers - same pattern as ProductList
  const columns = useMemo(() => {
    if (!allAdsData?.Column_header) return [];

    return generateDynamicColumns({
      columnHelper,
      headerData: allAdsData.Column_header,
      navigationConfig: {
        final_dest_url: {
          navigable: true,
          target: "_blank",
          getUrl: ({ originalRow, columnInfo }) => {
            return {
              pathname:
                originalRow?.[columnInfo.key]?.url ||
                originalRow?.[columnInfo.key]?.display,
              state: null,
            };
          },
        },
        page_name: {
          navigable: false, // Just display, don't make clickable
        },
      },
      filterConfig: {
        filterTypes: {
          status: "select",
          z_countries: "select",
        },
        excludeFromFilter: ["actions", "rowSelection"],
      },
      editableFields: {
        status: {
          inputType: "select",
        },
      },
    });
  }, [allAdsData?.Column_header]);

  // Table options - same pattern as ProductList
  const tableOptions = {
    enableGlobalFilter: true,
    enablePagination: true,
    enableColumnVisibility: true,
    enableSorting: true,
    enableColumnFilters: true,
    enableColumnOrder: true,
    enableColumnResize: true,
    enableRowSelection: false, // Disable for market ads
    defaultPageSize: pagination?.pageSize || 10,
    showTotalItems: true,
    showPreferences: true,
    // Server-side pagination
    manualPagination: true,
    pageCount: pagination?.totalPages || 1,
    state: {
      pagination: {
        pageIndex: (pagination?.currentPage || 1) - 1,
        pageSize: pagination?.pageSize || 10,
      },
    },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === "function"
          ? updater({
              pageIndex: (pagination?.currentPage || 1) - 1,
              pageSize: pagination?.pageSize || 10,
            })
          : updater;

      if (pagination?.onPageChange) {
        pagination.onPageChange(newPagination.pageIndex + 1);
      }
      if (
        pagination?.onPageSizeChange &&
        newPagination.pageSize !== pagination?.pageSize
      ) {
        pagination.onPageSizeChange(newPagination.pageSize);
      }
    },
  };

  // Transform chart data from prop
  const transformedChartData = useMemo(() => {
    if (!chartData?.data) return [];

    // Transform the chart data from the prop to the format expected by the chart
    return Object.entries(chartData.data).map(([date, value]) => ({
      date,
      value: Number(value),
    }));
  }, [chartData]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!allAdsData?.data || !Array.isArray(allAdsData.data)) {
      return {
        totalAds: 0,
        totalReach: 0,
        activeAds: 0,
      };
    }

    const totalAds = allAdsData.data.length;

    // Get total reach from the last index of chart data instead of calculating
    let totalReach;
    if (chartData?.data) {
      // Get the reach data from chart (should be an object with dates as keys)
      const reachData = chartData.data;

      // Get all the values and take the last one (most recent date = total reach)
      const reachValues = Object.values(reachData);
      const lastReachValue = reachValues[reachValues.length - 1];

      totalReach = parseInt(lastReachValue) || 0;
    } else {
      // Fallback to calculation if chart data is not available
      console.log("Chart data not available, falling back to calculation");
      totalReach = allAdsData.data.reduce((sum, item) => {
        const reach = parseInt(item.total_reach?.display || 0);
        return sum + reach;
      }, 0);
    }
    const activeAds = allAdsData.data.filter(
      (item) => item.status?.display === "passed"
    ).length;

    return {
      totalAds,
      totalReach,
      activeAds,
    };
  }, [allAdsData?.data]);

  return (
    <div className="space-y-4 w-full min-w-0">
      <Tabs defaultValue="ads" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="ads">Ads</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="ads">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {pagination?.totalItems || 0} items found
              </div>
              {pagination && (
                <div className="text-sm text-gray-600">
                  Showing{" "}
                  {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
                  {Math.min(
                    pagination.currentPage * pagination.pageSize,
                    pagination.totalItems
                  )}{" "}
                  of {pagination.totalItems} ads
                </div>
              )}
            </div>

            <TableTemplate
              columns={columns || []}
              data={allAdsData?.data || []}
              isLoading={isLoading}
              options={tableOptions}
              layout="standard"
              onServerSearch={onServerSearch}
              serverSearchValue={serverSearchValue}
              onServerFilter={onServerFilter}
              serverFilters={serverFilters}
              onServerSort={onServerSort}
              serverSortBy={serverSortBy}
              isPaginated={isPaginated}
            />
          </div>
        </TabsContent>

        <TabsContent value="analytics">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-blue-600">
                    {pagination?.totalItems || summaryStats.totalAds}
                  </div>
                  <p className="text-sm text-gray-600">Total Ads</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-green-600">
                    {summaryStats.totalReach.toLocaleString()}
                  </div>
                  <p className="text-sm text-gray-600">Total Reach</p>
                </CardContent>
              </Card>
              {/* <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-purple-600">
                    {pagination?.totalPages || 1}
                  </div>
                  <p className="text-sm text-gray-600">Pages</p>
                </CardContent>
              </Card> */}
              {/* COMMENTED OUT - Active Ads Card (4th card) */}
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-orange-600">
                    {summaryStats.activeAds}
                  </div>
                  <p className="text-sm text-gray-600">Active Ads</p>
                </CardContent>
              </Card>
            </div>

            <MarketLabChartTemplate
              title="Reach Performance Over Time"
              description="Market product reach trends"
              data={transformedChartData}
              footerText={
                transformedChartData.length > 0
                  ? "Showing reach data from market product"
                  : "No chart data available"
              }
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MarketProductAnalytics;
